import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createAutobookRuleGroup,
  patchAutobookRuleGroup,
  SaveApiResMessage,
} from '@/actions';
import appointmentStyles from '@/ctComponents/AppointmentTable/AppointmentTable.module.css';
import CloseButton from '@/ctComponents/closeButton';
import filterStyles from '@/ctComponents/Filter/styles.module.css';
import Modal from '@/ctComponents/Modal';
import SubmitButton from '@/ctComponents/submitButton';
import radioStyles from './AutobookRuleCustomerRadio.module.css';
import optionLabelStyles from './AutobookRuleOptionLabel.module.css';

const inputStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
};

const LIST_BLOCK_MAX_H = 160;

const CUSTOMERS_LIST_HEIGHT = 104;
function customerListScrollBoxStyle() {
  return {
    height: CUSTOMERS_LIST_HEIGHT,
    minHeight: CUSTOMERS_LIST_HEIGHT,
    maxHeight: CUSTOMERS_LIST_HEIGHT,
    boxSizing: 'border-box',
    flex: `0 0 ${CUSTOMERS_LIST_HEIGHT}px`,
  };
}

function possessiveName(name) {
  const t = (name || '').trim();
  if (!t) return '';
  const last = t[t.length - 1];
  if (last === 's' || last === 'S') return `${t}'`;
  return `${t}'s`;
}

function warehouseSectionTitle(focusedCustomerId, customersById) {
  if (!focusedCustomerId) return 'Warehouses';
  const label = customersById[focusedCustomerId]?.label ?? 'Customer';
  return `${possessiveName(label)} Warehouses`;
}

function warehouseUuidsForCustomer(allWarehouses, customerId) {
  return allWarehouses
    .filter((w) => w.customerId === customerId && w.uuid)
    .map((w) => w.uuid);
}

const WarehouseGroupModalCustomerRow = ({
  customerId,
  label,
  warehouses,
  warehouseUuids,
  onToggleAllWarehouses,
  onLabelClick,
  isWarehouseListFocused,
}) => {
  const inputRef = useRef(null);
  const keys = useMemo(
    () => warehouseUuidsForCustomer(warehouses, customerId),
    [warehouses, customerId]
  );
  const n = keys.length;
  const selectedCount = useMemo(
    () => keys.filter((u) => warehouseUuids.has(u)).length,
    [keys, warehouseUuids]
  );
  const allSelected = n > 0 && selectedCount === n;
  const someSelected = selectedCount > 0 && selectedCount < n;

  useEffect(() => {
    const el = inputRef.current;
    if (el) el.indeterminate = someSelected;
  }, [someSelected, allSelected, n]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${filterStyles.option} ${optionLabelStyles.optionRow} ${radioStyles.customerRow}${
        isWarehouseListFocused ? ` ${radioStyles.customerRowFocused}` : ''
      }`}
      onClick={() => onLabelClick(customerId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onLabelClick(customerId);
        }
      }}>
      <input
        ref={inputRef}
        type="checkbox"
        className={`${filterStyles.checkBox} ${radioStyles.checkBoxMaster} shrink-0`}
        checked={allSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggleAllWarehouses(customerId)}
      />
      <span
        className={`${optionLabelStyles.labelText} flex-1 min-w-0 max-w-none select-none`}
        title={label}>
        {label}
      </span>
    </div>
  );
};

function groupUuidForApi(g) {
  if (!g || typeof g !== 'object') return '';
  const u = g.uuid ?? g.id;
  return u != null ? String(u).trim() : '';
}

export const AutobookRuleWarehouseGroupModal = ({
  show,
  setShow,
  mode = 'create',
  editingGroup = null,
  onCreated,
  onUpdated,
  customers = [],
  warehouses = [],
}) => {
  const [groupName, setGroupName] = useState('');
  const [focusedCustomerId, setFocusedCustomerId] = useState(null);
  const [warehouseUuids, setWarehouseUuids] = useState(() => new Set());
  const [creating, setCreating] = useState(false);

  const customersForPicker = useMemo(
    () => customers.filter((c) => c.uuid),
    [customers]
  );

  const customersById = useMemo(() => {
    const m = {};
    customersForPicker.forEach((c) => {
      m[c.id] = c;
    });
    return m;
  }, [customersForPicker]);

  const visibleWarehouses = useMemo(() => {
    if (!focusedCustomerId) return [];
    return warehouses.filter(
      (w) => w.customerId === focusedCustomerId && w.uuid
    );
  }, [focusedCustomerId, warehouses]);

  const customerUuidsForSubmit = useMemo(() => {
    const s = new Set();
    warehouses.forEach((w) => {
      if (!w.uuid || !warehouseUuids.has(w.uuid)) return;
      const c = customersForPicker.find((x) => x.id === w.customerId);
      if (c?.uuid) s.add(String(c.uuid).trim());
    });
    return [...s].filter(Boolean);
  }, [warehouseUuids, warehouses, customersForPicker]);

  const warehouseTitle = warehouseSectionTitle(
    focusedCustomerId,
    customersById
  );

  const reset = useCallback(() => {
    setGroupName('');
    setFocusedCustomerId(null);
    setWarehouseUuids(new Set());
  }, []);

  const seedKeyRef = useRef('');
  useEffect(() => {
    if (!show) {
      reset();
      seedKeyRef.current = '';
      return;
    }
    const key =
      mode === 'edit' && editingGroup
        ? `edit:${editingGroup.id}`
        : 'create';
    if (seedKeyRef.current === key) return;
    seedKeyRef.current = key;

    if (mode === 'edit' && editingGroup) {
      const wh = editingGroup.groupWarehouseUuids ?? [];
      const whNorm = wh.map((u) => String(u).trim()).filter(Boolean);
      setGroupName(String(editingGroup.name ?? ''));
      setWarehouseUuids(new Set(whNorm));
      const withUuid = customers.filter((c) => c.uuid);
      const firstCust = withUuid.find((c) =>
        warehouses.some(
          (w) =>
            w.customerId === c.id &&
            w.uuid &&
            whNorm.includes(String(w.uuid).trim())
        )
      );
      setFocusedCustomerId(firstCust?.id ?? null);
    } else {
      reset();
    }
  }, [show, mode, editingGroup, reset, customers, warehouses]);

  const focusCustomerByLabel = useCallback((customerId) => {
    setFocusedCustomerId(customerId);
  }, []);

  const toggleAllWarehousesForCustomer = useCallback(
    (customerId) => {
      const uuids = warehouseUuidsForCustomer(warehouses, customerId);
      if (uuids.length === 0) return;
      setWarehouseUuids((prev) => {
        const next = new Set(prev);
        const allOn = uuids.every((u) => next.has(u));
        if (allOn) {
          uuids.forEach((u) => next.delete(u));
        } else {
          uuids.forEach((u) => next.add(u));
        }
        return next;
      });
    },
    [warehouses]
  );

  const toggleWarehouse = useCallback((uuid) => {
    setWarehouseUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);

  const canSubmit =
    groupName.trim().length > 0 &&
    warehouseUuids.size > 0 &&
    customerUuidsForSubmit.length > 0;

  const handleSubmit = useCallback(() => {
    const name = groupName.trim().slice(0, 255);
    const customerUuidsPayload = [...customerUuidsForSubmit];
    const warehouseUuidsPayload = [
      ...new Set(
        [...warehouseUuids]
          .map((u) => String(u).trim())
          .filter(Boolean)
      ),
    ];
    if (
      !name ||
      customerUuidsPayload.length === 0 ||
      warehouseUuidsPayload.length === 0 ||
      creating
    ) {
      return;
    }
    const payload = {
      name,
      customers: customerUuidsPayload,
      warehouses: warehouseUuidsPayload,
    };

    if (mode === 'edit') {
      const gid = groupUuidForApi(editingGroup);
      if (!gid || !editingGroup) return;
      setCreating(true);
      patchAutobookRuleGroup(gid, payload)
        .then((body) => {
          const g =
            body?.data?.group ??
            body?.group ??
            body?.data?.data?.group ??
            null;
          const resolvedName = g?.name ?? name;
          SaveApiResMessage({
            title: body?.message || 'Success',
            message: 'Warehouse group updated successfully',
            mode: 'success',
          });
          if (onUpdated) {
            onUpdated({
              id: editingGroup.id,
              uuid: gid,
              name: resolvedName,
              groupWarehouseUuids: warehouseUuidsPayload,
              groupCustomerUuids: customerUuidsPayload,
            });
          }
          setShow(false);
          reset();
        })
        .catch((err) => {
          SaveApiResMessage(err);
        })
        .finally(() => {
          setCreating(false);
        });
      return;
    }

    setCreating(true);
    createAutobookRuleGroup(payload)
      .then((body) => {
        const g =
          body?.data?.group ??
          body?.group ??
          body?.data?.data?.group ??
          null;
        const id =
          g?.uuid ??
          g?.id ??
          body?.data?.uuid ??
          `grp-local-${Date.now()}`;
        const resolvedName = g?.name ?? name;
        if (onCreated) {
          onCreated({
            id: String(id),
            uuid: g?.uuid ?? String(id),
            name: resolvedName,
            groupWarehouseUuids: warehouseUuidsPayload,
            groupCustomerUuids: customerUuidsPayload,
          });
        }
        setShow(false);
        reset();
      })
      .catch((err) => {
        SaveApiResMessage(err);
      })
      .finally(() => {
        setCreating(false);
      });
  }, [
    creating,
    groupName,
    customerUuidsForSubmit,
    warehouseUuids,
    mode,
    editingGroup,
    onCreated,
    onUpdated,
    setShow,
    reset,
  ]);

  const isEdit = mode === 'edit';

  return (
    <Modal
      show={show}
      setShow={setShow}
      manageScroll={false}
      overlayZIndex={200000000}
      minWidth={560}
      maxHeight={720}
      icon={isEdit ? 'saveData' : 'Add'}
      title={isEdit ? 'Edit warehouse group' : 'Create warehouse group'}
      titleColor="#131517">
      <div className="box-border flex w-full min-w-0 max-w-full flex-col gap-4 overflow-x-hidden py-[20px]">
        <div className={`${appointmentStyles.LicenseContainer} w-full min-w-0`}>
          <span>Group name</span>
          <input
            className={appointmentStyles.LicenseInput}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Enter group name"
            style={inputStyle}
          />
        </div>

        <section className="flex min-h-0 min-w-0 w-full flex-col self-start rounded-lg border border-solid border-[#e8e8e8] bg-white p-3">
          <h3 className="m-0 mb-3 shrink-0 truncate text-left text-sm font-bold leading-tight text-[#131517]">
            Customers
          </h3>
          <div
            className="w-full min-w-0 overflow-x-hidden overflow-y-auto pr-1"
            style={customerListScrollBoxStyle()}>
            <div
              className="grid min-w-0 gap-2"
              style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              {customersForPicker.map((c) => (
                <WarehouseGroupModalCustomerRow
                  key={c.id}
                  customerId={c.id}
                  label={c.label}
                  warehouses={warehouses}
                  warehouseUuids={warehouseUuids}
                  onToggleAllWarehouses={toggleAllWarehousesForCustomer}
                  onLabelClick={focusCustomerByLabel}
                  isWarehouseListFocused={focusedCustomerId === c.id}
                />
              ))}
            </div>
          </div>
        </section>

        <div>
          <p className="m-0 mb-2 text-left text-xs font-bold text-[#131517]">
            {warehouseTitle}
          </p>
          <div
            className="overflow-y-auto rounded-md bg-[#F5F7FA7A] p-2"
            style={{ maxHeight: LIST_BLOCK_MAX_H }}>
            {visibleWarehouses.length > 0 ? (
              <div
                className="grid min-w-0"
                style={{
                  gap: '0.03rem',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                }}>
                {visibleWarehouses.map((w) => {
                  const u = w.uuid;
                  return (
                    <label
                      key={w.uuid}
                      className={`${filterStyles.option} ${optionLabelStyles.optionRow} ${radioStyles.warehouseRow}`}>
                      <input
                        type="checkbox"
                        className={`${filterStyles.checkBox} shrink-0`}
                        checked={warehouseUuids.has(u)}
                        onChange={() => toggleWarehouse(u)}
                      />
                      <span
                        className={optionLabelStyles.labelText}
                        title={w.label}>
                        {w.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="flex min-h-[120px] items-center justify-center px-2 text-center">
                <p className="m-0 max-w-[220px] text-xs font-bold leading-snug text-[#99a0ae]">
                  Click a customer name to view their warehouses
                </p>
              </div>
            )}
          </div>
          {visibleWarehouses.length > 0 && (
            <p className="mb-0 mt-2 text-left text-xs leading-snug text-inactive-text">
              {warehouseUuids.size} selected
            </p>
          )}
        </div>

        <div className="mt-1 flex w-full flex-row items-center justify-end gap-3">
          <CloseButton text="Cancel" onClick={() => setShow(false)} />
          <SubmitButton
            text={isEdit ? 'Save' : 'Create'}
            onClick={handleSubmit}
            disable={!canSubmit || creating}
            loading={creating}
          />
        </div>
      </div>
    </Modal>
  );
};
